import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Zap, Shield, Clock, Award } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// HomeLyph product family hero image
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_b00a3930-4b93-4b69-b5e6-d5270a23f55a/artifacts/sh06iq3k_HomeLyph_Family_Shot_no_logos.jpg";

export const HeroSection = () => {
  const [heroBanner, setHeroBanner] = useState(null);

  useEffect(() => {
    const fetchHeroBanner = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/marketing/banners/active?location=homepage`);
        if (res.data && res.data.length > 0) {
          setHeroBanner(res.data[0]);
        }
      } catch (err) {
        // Silent fail - use defaults
      }
    };
    fetchHeroBanner();
  }, []);

  // Default values if no banner configured
  const badgeText = heroBanner?.title || 'New Year Sale - Save Up to $500';
  const ctaLink = heroBanner?.cta_link || '/products';

  return (
    <section className="relative min-h-screen overflow-hidden bg-[#1a1a1a]">
      {/* Background Image - Product Family Shot */}
      <div className="absolute inset-0">
        <img 
          src={HERO_IMAGE}
          alt="HomeLyph Freeze Dryer Family - Compact, Standard, and Commercial models"
          className="w-full h-full object-cover object-center"
        />
        {/* Gradient overlay - darker at top for text, subtle at bottom for product visibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0203]/85 via-[#0a0203]/40 to-[#0a0203]/60" />
        {/* Side gradient for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0203]/70 via-transparent to-transparent" />
      </div>
      
      {/* Content Container */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-36 md:pt-44 pb-24">
        {/* Top Section - Brand & Headlines */}
        <div className="text-center mb-12 md:mb-16">
          {/* Sale Badge - Dynamic from Marketing Banners */}
          <Link 
            to={ctaLink} 
            className="inline-flex items-center gap-2 bg-[#fdc31c] text-[#0a0203] px-5 py-2.5 rounded-full text-sm font-bold mb-8 animate-fade-in hover:bg-[#e5b019] transition-all hover:scale-105 shadow-lg" 
            data-testid="hero-sale-badge"
          >
            <Zap className="h-4 w-4" />
            {badgeText}
          </Link>
          
          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-white leading-tight mb-6 animate-slide-up tracking-tight">
            The Complete
            <span className="block text-[#fdc31c] mt-2">Freeze Dryer Lineup</span>
          </h1>
          
          <p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto animate-slide-up leading-relaxed" style={{ animationDelay: '100ms' }}>
            From compact home units to commercial powerhouses. 
            <span className="text-white font-medium"> Preserve food for 25+ years</span> with industry-leading technology.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4 animate-slide-up" style={{ animationDelay: '200ms' }}>
            <Link to="/products">
              <Button className="bg-[#fdc31c] hover:bg-[#e5b019] text-[#0a0203] font-bold px-8 py-6 text-lg btn-scale shadow-xl shadow-[#fdc31c]/20" data-testid="hero-shop-btn">
                Explore Our Models <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/compare">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#003456] px-8 py-6 text-lg btn-scale backdrop-blur-sm" data-testid="hero-compare-btn">
                Compare Models
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Product Size Indicators - Desktop Only */}
        <div className="hidden lg:flex justify-center items-end gap-16 mt-auto pt-32 animate-slide-up" style={{ animationDelay: '400ms' }}>
          {/* HomeLyph M */}
          <div className="text-center group cursor-pointer">
            <Link to="/products?size=Small" className="block">
              <div className="mb-3 transform group-hover:-translate-y-2 transition-transform duration-300">
                <span className="inline-block px-4 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium border border-white/20 group-hover:bg-[#fdc31c] group-hover:text-[#0a0203] group-hover:border-[#fdc31c] transition-all">
                  HomeLyph M
                </span>
              </div>
              <p className="text-white/60 text-xs">Up to 15 lbs/batch</p>
            </Link>
          </div>
          
          {/* HomeLyph L */}
          <div className="text-center group cursor-pointer">
            <Link to="/products?size=Medium" className="block">
              <div className="mb-3 transform group-hover:-translate-y-2 transition-transform duration-300">
                <span className="inline-block px-4 py-1.5 bg-[#fdc31c]/90 backdrop-blur-sm rounded-full text-[#0a0203] text-sm font-bold border border-[#fdc31c] group-hover:bg-[#fdc31c] transition-all shadow-lg">
                  HomeLyph L - Best Seller
                </span>
              </div>
              <p className="text-white/60 text-xs">18-20 lbs/batch</p>
            </Link>
          </div>
          
          {/* HomeLyph XL */}
          <div className="text-center group cursor-pointer">
            <Link to="/products?size=XL" className="block">
              <div className="mb-3 transform group-hover:-translate-y-2 transition-transform duration-300">
                <span className="inline-block px-4 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium border border-white/20 group-hover:bg-[#fdc31c] group-hover:text-[#0a0203] group-hover:border-[#fdc31c] transition-all">
                  HomeLyph XL
                </span>
              </div>
              <p className="text-white/60 text-xs">40-45 lbs/batch</p>
            </Link>
          </div>
        </div>
      </div>
      
      {/* Bottom Feature Bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0a0203] to-transparent">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-wrap justify-center md:justify-between items-center gap-6 text-white/70">
            <div className="flex items-center gap-2 text-sm">
              <Shield className="h-5 w-5 text-[#fdc31c]" />
              <span>2-Year Warranty</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-5 w-5 text-[#fdc31c]" />
              <span>25+ Year Shelf Life</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Award className="h-5 w-5 text-[#fdc31c]" />
              <span>Assembled in North America</span>
            </div>
            <Link to="/financing" className="flex items-center gap-2 text-sm hover:text-[#fdc31c] transition-colors">
              <span className="font-medium">Financing from $67/mo</span>
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 animate-bounce hidden md:block">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-white/50 rounded-full" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
